import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Stack,
  Modal,
  ModalDialog,
  ModalClose,
  Input,
  Textarea,
  FormControl,
  FormLabel,
  FormHelperText,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  Divider,
  Select,
  Option,
  Checkbox,
  Avatar,
  Tooltip,
} from '@mui/joy';
import {
  Phone as PhoneIcon,
  MagnifyingGlass as SearchIcon,
  Plus as AddIcon,
  Trash as DeleteIcon,
  PencilSimple as EditIcon,
  ArrowsClockwise as SyncIcon,
  User as PersonIcon,
  Star as StarIcon,
} from '@phosphor-icons/react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../lib/api';
import { useOrganizationUsers } from '../../hooks/useOrganizationUsers';
import { useCanIncurCharges } from '../../hooks/useSubscriptionStatus';
import type { TwilioPhoneNumber, AvailablePhoneNumber, AssignedUser } from '../../types/inbox';
import { PageHelpButton } from '../../components/knowledge-base/PageHelpButton';
import { CarrierRegistrationCard } from '../../components/settings/CarrierRegistrationCard';
import { SMSConsentManager } from '../../components/settings/SMSConsentManager';
import { CommunicationsCancellation } from '../../components/settings/CommunicationsCancellation';
import { LegacyRentalActivation } from '../../components/settings/LegacyRentalActivation';
import { ResponsiveTable } from '../../components/common/ResponsiveTable';
import { useAuth } from '../../contexts/AuthContext';
import { communicationError, postCommunication, deliveryLabel } from '../../lib/communicationRequests';
import type { NumberQuote, NumberOperation, MessagingReadiness } from '../../types/communications';

/** Names shown in the Assigned Users column before it collapses to "+N more". */
const MAX_ASSIGNEES_SHOWN = 3;

const NUMBERS_NEED_PAID_PLAN =
  'Ordering a phone number requires an active paid plan. Numbers bill monthly, ' +
  'so they are not available during the trial.';

const PhoneNumbersPage: React.FC = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isAdmin = !!(user?.is_superuser || user?.is_organization_admin);
  const [quote, setQuote] = useState<NumberQuote | null>(null);
  const [acceptedQuote, setAcceptedQuote] = useState(false);
  const [releaseConfirmation, setReleaseConfirmation] = useState('');
  const [notice, setNotice] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  const readiness = useQuery({ queryKey: ['messaging-readiness', user?.organization?.id], queryFn: async () => (await api.get<MessagingReadiness>('/api/inbox/a2p-registration/readiness/')).data, refetchInterval: 15000 });
  const operations = useQuery({ queryKey: ['number-operations', user?.organization?.id], queryFn: async () => (await api.get<{ results: NumberOperation[] }>('/api/inbox/phone-numbers/operations/')).data.results, enabled: isAdmin, refetchInterval: 15000 });

  // undefined while loading — don't flash a disabled button before we know.
  const canOrderNumbers = useCanIncurCharges();
  const orderingBlocked = !isAdmin || canOrderNumbers !== true || !readiness.data?.is_approved;

  // Modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showReleaseConfirm, setShowReleaseConfirm] = useState(false);
  const [selectedNumber, setSelectedNumber] = useState<TwilioPhoneNumber | null>(null);

  // Search state
  const [areaCode, setAreaCode] = useState('');
  const [searchResults, setSearchResults] = useState<AvailablePhoneNumber[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  // Edit state
  const [editForwardTo, setEditForwardTo] = useState('');
  const [editFriendlyName, setEditFriendlyName] = useState('');
  const [editGreeting, setEditGreeting] = useState('');
  const [editScreenCalls, setEditScreenCalls] = useState(true);

  // User assignment state
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [assignCanSendSms, setAssignCanSendSms] = useState(true);
  const [assignCanMakeCalls, setAssignCanMakeCalls] = useState(true);

  const [actionError, setActionError] = useState<string | null>(null);

  // Fetch organization users
  const { users } = useOrganizationUsers();

  // Fetch org's purchased numbers
  const { data: numbersData, isLoading, isError: numbersError, refetch: reloadNumbers } = useQuery({
    queryKey: ['phone-numbers', user?.organization?.id],
    queryFn: () => api.get<{ results: TwilioPhoneNumber[] }>('/api/inbox/phone-numbers/'),
  });

  const phoneNumbers: TwilioPhoneNumber[] = (() => {
    const d = numbersData?.data as any;
    if (!d) return [];
    if (Array.isArray(d)) return d;
    if (Array.isArray(d.results)) return d.results;
    return [];
  })();

  useEffect(() => {
    if (selectedNumber) {
      const latest = phoneNumbers.find(number => number.id === selectedNumber.id);
      if (latest && latest !== selectedNumber) setSelectedNumber(latest);
    }
  }, [numbersData]);
  const quoteMutation = useMutation({
    mutationFn: (phone_number: string) => api.post<NumberQuote>('/api/inbox/phone-numbers/quote/', { phone_number }),
    onSuccess: response => { setQuote(response.data); setAcceptedQuote(false); setActionError(null); },
    onError: error => setActionError(communicationError(error)),
  });
  const editAssignment = useMutation({
    mutationFn: ({ id, changes }: { id: number; changes: Partial<AssignedUser> }) => api.patch(`/api/inbox/phone-number-assignments/${id}/`, changes),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['phone-numbers'] }),
    onError: error => setActionError(communicationError(error)),
  });

  const purchaseMutation = useMutation({
    mutationFn: () => postCommunication<NumberOperation>('/api/inbox/phone-numbers/purchase/', {
      phone_number: quote!.phone_number, quote_token: quote!.quote_token, accept_fees: acceptedQuote,
    }, { phone_number: quote!.phone_number }),
    onSuccess: response => {
      queryClient.invalidateQueries({ queryKey: ['phone-numbers'] });
      queryClient.invalidateQueries({ queryKey: ['number-operations'] });
      setNotice(`Number order: ${deliveryLabel(response.data.status)}. ${response.data.error || 'Texting requires confirmed carrier activation and a user assignment.'}`);
      setShowAddModal(false); setQuote(null); setSearchResults([]); setActionError(null);
    },
    onError: error => { setActionError(communicationError(error)); queryClient.invalidateQueries({ queryKey: ['number-operations'] }); },
  });

  // Sync mutation
  const syncMutation = useMutation({
    mutationFn: () => api.post<{ synced: number; errors: { error?: string }[] }>('/api/inbox/phone-numbers/sync/', {}),
    onSuccess: response => {
      setNotice(`Checked ${response.data.synced} owned numbers. ${response.data.errors.length} need review. This does not import other businesses’ numbers or prove carrier activation.`);
      queryClient.invalidateQueries({ queryKey: ['phone-numbers'] });
      setActionError(null);
    },
    onError: (err: any) => {
      setActionError(err?.responseData?.error || 'Sync failed');
    },
  });

  // Release mutation
  const releaseMutation = useMutation({
    mutationFn: (id: number) =>
      postCommunication<NumberOperation>(`/api/inbox/phone-numbers/${id}/release/`, { confirm_number: releaseConfirmation }),
    onSuccess: response => {
      setNotice(`Number release: ${deliveryLabel(response.data.status)}. ${response.data.error || 'Rental billing stops after provider-confirmed release.'}`);
      queryClient.invalidateQueries({ queryKey: ['number-operations'] });
      queryClient.invalidateQueries({ queryKey: ['phone-numbers'] });
      setShowReleaseConfirm(false);
      setSelectedNumber(null);
      setActionError(null);
    },
    onError: (err: any) => {
      setActionError(err?.responseData?.error || 'Failed to release number');
    },
  });

  // Update (forward_to_number) mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<TwilioPhoneNumber> }) =>
      api.patch(`/api/inbox/phone-numbers/${id}/`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['phone-numbers'] });
      setShowEditModal(false);
      setSelectedNumber(null);
      setActionError(null);
    },
    onError: (err: any) => {
      setActionError(err?.responseData?.error || 'Failed to update number');
    },
  });

  // Assign user mutation
  const assignUserMutation = useMutation({
    mutationFn: (data: { user: number; phone_number: number; can_send_sms: boolean; can_make_calls: boolean }) =>
      api.post('/api/inbox/phone-number-assignments/', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['phone-numbers'] });
      setSelectedUserId(null);
      setAssignCanSendSms(true);
      setAssignCanMakeCalls(true);
      setActionError(null);
    },
    onError: (err: any) => {
      setActionError(err?.responseData?.error || 'Failed to assign user');
    },
  });

  // Remove assignment mutation
  const removeAssignmentMutation = useMutation({
    mutationFn: (assignmentId: number) =>
      api.delete(`/api/inbox/phone-number-assignments/${assignmentId}/`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['phone-numbers'] });
      setActionError(null);
    },
    onError: (err: any) => {
      setActionError(err?.responseData?.error || 'Failed to remove assignment');
    },
  });

  // Set default mutation
  const setDefaultMutation = useMutation({
    mutationFn: (assignmentId: number) =>
      api.post(`/api/inbox/phone-number-assignments/${assignmentId}/set-default/`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['phone-numbers'] });
      setActionError(null);
    },
    onError: (err: any) => {
      setActionError(err?.responseData?.error || 'Failed to set default');
    },
  });

  const handleSearch = async () => {
    if (!areaCode.trim()) return;
    setSearching(true);
    setHasSearched(true);
    setQuote(null);
    setAcceptedQuote(false);
    setSearchError(null);
    try {
      const res = await api.get<{ results: AvailablePhoneNumber[] }>(
        `/api/inbox/phone-numbers/search-available/?area_code=${encodeURIComponent(areaCode.trim())}`
      );
      const d = res.data as any;
      setSearchResults(Array.isArray(d?.results) ? d.results : []);
    } catch (err: any) {
      setSearchError(err?.responseData?.error || 'Search failed');
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  const formatPhone = (phone: string) => {
    const digits = phone.replace(/\D/g, '');
    if (digits.length === 11 && digits[0] === '1') {
      return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
    }
    return phone;
  };

  const openEdit = (num: TwilioPhoneNumber) => {
    setSelectedNumber(num);
    setEditForwardTo(num.forward_to_number || '');
    setEditFriendlyName(num.friendly_name || '');
    setEditGreeting(num.voicemail_greeting || '');
    setEditScreenCalls(num.screen_forwarded_calls ?? true);
    setSelectedUserId(null);
    setAssignCanSendSms(true);
    setAssignCanMakeCalls(true);
    setActionError(null);
    setShowEditModal(true);
  };

  const handleAssignUser = () => {
    if (!selectedUserId || !selectedNumber) return;
    assignUserMutation.mutate({
      user: selectedUserId,
      phone_number: selectedNumber.id,
      can_send_sms: assignCanSendSms,
      can_make_calls: assignCanMakeCalls,
    });
  };

  // Get users not already assigned to the selected number
  const availableUsers = users.filter(
    (u) => !selectedNumber?.assigned_users?.some((a) => a.user_id === u.id)
  );

  const openRelease = (num: TwilioPhoneNumber) => {
    setSelectedNumber(num);
    setActionError(null);
    setReleaseConfirmation('');
    setShowReleaseConfirm(true);
  };

  return (
    <Box>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} spacing={1.5} sx={{ mb: 3 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography level="h3" sx={{ fontSize: { xs: '1.25rem', md: undefined } }}>Communications</Typography>
            <PageHelpButton />
          </Box>
          <Typography level="body-sm" color="neutral">
            Register your business for texting, then add numbers and assign calling and messaging access.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} sx={{ '& > button': { flex: { xs: 1, md: '0 0 auto' } } }}>
          <Button
            variant="outlined"
            color="primary"
            startDecorator={<SyncIcon />}
            loading={syncMutation.isPending}
            disabled={!isAdmin}
            onClick={() => syncMutation.mutate()}
          >
            Check owned numbers
          </Button>
          <Tooltip title={orderingBlocked ? NUMBERS_NEED_PAID_PLAN : ''} variant="soft">
            <span>
              <Button
                startDecorator={<AddIcon />}
                disabled={orderingBlocked}
                onClick={() => { setShowAddModal(true); setQuote(null); setAcceptedQuote(false); setHasSearched(false); setSearchResults([]); setAreaCode(''); setActionError(null); }}
                sx={{ width: '100%' }}
              >
                Add Number
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      {canOrderNumbers === false && (
        <Alert color="warning" variant="soft" sx={{ mb: 2 }}>
          {NUMBERS_NEED_PAID_PLAN}
        </Alert>
      )}

      {/* Carrier registration sits above the numbers: an unregistered business
          can own numbers, but its texts are the ones carriers filter. */}
      <CarrierRegistrationCard />
      <Typography level="title-lg" sx={{ mb: 1 }}>4. Rent and activate a number</Typography>
      <Typography level="body-sm" sx={{ mb: 2 }}>After campaign approval, review a US local number quote. Carrier activation is a separate step; assign staff only to the access they need. Existing numbers with unknown status require support reconciliation.</Typography>
      {notice && <Alert sx={{ mb: 2 }} color="neutral" endDecorator={<Button variant="plain" onClick={() => setNotice('')}>Dismiss</Button>}>{notice}</Alert>}
      {actionError && !showAddModal && !showEditModal && !showReleaseConfirm && <Alert color="danger" sx={{ mb: 2 }}>{actionError}</Alert>}
      {numbersError && <Alert color="danger" endDecorator={<Button onClick={() => reloadNumbers()}>Retry</Button>}>Could not load your numbers. This does not mean you have no numbers.</Alert>}


      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : numbersError ? null : phoneNumbers.length === 0 ? (
        <Card variant="outlined">
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <PhoneIcon size={48} color="var(--joy-palette-text-secondary)" style={{ marginBottom: 16 }} />
            <Typography level="body-lg" sx={{ mb: 1 }}>No phone numbers yet</Typography>
            <Typography level="body-sm" color="neutral" sx={{ mb: 2 }}>
              Complete campaign approval, review a number quote, then wait for carrier activation before texting.
            </Typography>
            <Stack direction="row" spacing={1} justifyContent="center">
              <Button
                variant="outlined"
                color="primary"
                startDecorator={<SyncIcon />}
                loading={syncMutation.isPending}
                disabled={!isAdmin}
            onClick={() => syncMutation.mutate()}
              >
                Check owned numbers
              </Button>
              <Tooltip title={orderingBlocked ? NUMBERS_NEED_PAID_PLAN : ''} variant="soft">
                <span>
                  <Button
                    startDecorator={<AddIcon />}
                    disabled={orderingBlocked}
                    onClick={() => { setShowAddModal(true); setQuote(null); setAcceptedQuote(false); setHasSearched(false); setSearchResults([]); setAreaCode(''); }}
                  >
                    Add Number
                  </Button>
                </span>
              </Tooltip>
            </Stack>
          </CardContent>
        </Card>
      ) : (
        <ResponsiveTable<TwilioPhoneNumber> rows={phoneNumbers} getRowKey={number => number.id}
          columns={[
            { key: 'number', header: 'Number', mobileVariant: 'header', render: number => <Box><Typography fontWeight="lg">{formatPhone(number.phone_number)}</Typography><Typography level="body-xs">{number.friendly_name || 'Unnamed number'}</Typography></Box> },
            { key: 'activation', header: 'Texting activation', mobileVariant: 'body', render: number => number.is_platform_number ? <Stack spacing={0.5}><Chip size="sm" color="neutral">Platform number</Chip><Typography level="body-xs">Sends ApexOS account texts. Managed by ApexOS support.</Typography></Stack> : <Stack spacing={0.5}><Chip size="sm" color={number.sms_ready ? 'success' : 'warning'}>{number.sms_ready ? 'Ready for you to text' : deliveryLabel(number.carrier_status || 'unknown')}</Chip><Typography level="body-xs">{number.readiness_reason}</Typography>{number.carrier_error && <Typography level="body-xs" color="danger">{number.carrier_error}</Typography>}</Stack> },
            { key: 'users', header: 'Assigned users', render: number => <Typography level="body-sm">{number.assigned_users?.slice(0, MAX_ASSIGNEES_SHOWN).map(a => a.user_name).join(', ') || 'None'}{(number.assigned_users?.length || 0) > MAX_ASSIGNEES_SHOWN && ` +${number.assigned_users!.length - MAX_ASSIGNEES_SHOWN} more`}</Typography> },
            { key: 'calls', header: 'Calls', render: number => <Typography level="body-sm">{number.voice_enabled ? number.forward_to_number ? formatPhone(number.forward_to_number) : 'Voicemail' : 'Not supported'}</Typography> },
            { key: 'rental', header: 'Rental', render: number => number.is_platform_number ? <Typography level="body-xs">Managed by ApexOS</Typography> : <Stack><Typography level="body-xs">{number.released_at ? `Released ${new Date(number.released_at).toLocaleDateString()}` : number.release_requested_at ? 'Release requested — confirmation pending' : number.renews_at ? `Renews ${new Date(number.renews_at).toLocaleDateString()}` : 'Legacy billing — review needed'}</Typography>{isAdmin && number.is_active && !number.renews_at && !number.release_requested_at && <LegacyRentalActivation numberId={number.id} phoneNumber={number.phone_number} />}</Stack> },
          ]}
          rowActions={isAdmin ? number => <Stack direction="row"><IconButton aria-label={`Edit ${number.phone_number}`} onClick={() => openEdit(number)}><EditIcon /></IconButton>{!number.is_platform_number && <IconButton aria-label={`Release ${number.phone_number}`} color="danger" disabled={!number.is_active || !!number.release_requested_at} onClick={() => openRelease(number)}><DeleteIcon /></IconButton>}</Stack> : undefined}
        />
      )}
      {isAdmin && <Card sx={{ mt: 3 }}><Typography level="title-md">Number order and release history</Typography>
        {operations.isError && <Alert color="warning">Could not load order history.</Alert>}
        {!operations.data?.length && !operations.isError && <Typography level="body-sm">No recent number operations.</Typography>}
        {operations.data?.map(operation => <Box key={operation.id} sx={{ py: 1 }}><Typography level="body-sm">{operation.phone_number} · {operation.kind} · {deliveryLabel(operation.status)}</Typography><Typography level="body-xs">{new Date(operation.created_at).toLocaleString()} · Request {operation.id}</Typography>{operation.error && <Typography level="body-sm" color="warning">{operation.error}</Typography>}</Box>)}
      </Card>}
      <SMSConsentManager />
      {isAdmin && <CommunicationsCancellation />}

      {/* Add Number Modal */}
      <Modal open={showAddModal} onClose={() => setShowAddModal(false)}>
        <ModalDialog sx={{ width: 700, maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto' }}>
          <ModalClose />
          <Typography level="h4" sx={{ mb: 2 }}>Add Phone Number</Typography>

          {actionError && (
            <Alert color="danger" sx={{ mb: 2 }}>{actionError}</Alert>
          )}

          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            <FormControl sx={{ flex: 1 }}>
              <FormLabel>Area Code</FormLabel>
              <Input
                placeholder="e.g. 832"
                value={areaCode}
                onChange={(e) => setAreaCode(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
              />
            </FormControl>
            <Box sx={{ pt: '28px' }}>
              <Button
                startDecorator={searching ? <CircularProgress size="sm" /> : <SearchIcon />}
                onClick={handleSearch}
                disabled={!areaCode.trim() || searching}
              >
                Search
              </Button>
            </Box>
          </Stack>

          {searchError && <Alert color="danger" sx={{ mb: 2 }}>{searchError}</Alert>}

          {searchResults.length > 0 && (
            <>
              <Divider sx={{ my: 1 }} />
              <Typography level="body-sm" color="neutral" sx={{ mb: 1 }}>
                {searchResults.length} available numbers
              </Typography>
              <ResponsiveTable<AvailablePhoneNumber> rows={searchResults} getRowKey={number => number.phone_number}
                columns={[{ key: 'number', header: 'Number', mobileVariant: 'header', render: number => formatPhone(number.phone_number) },
                  { key: 'location', header: 'Location', render: number => [number.locality, number.region].filter(Boolean).join(', ') }]}
                rowActions={number => <Button size="sm" variant="outlined" loading={quoteMutation.isPending} disabled={purchaseMutation.isPending} onClick={() => quoteMutation.mutate(number.phone_number)}>Review quote</Button>} />

            </>
          )}

          {searchResults.length === 0 && !searching && areaCode && !searchError && (
            <Typography level="body-sm" color="neutral">
              {hasSearched ? 'No numbers available in that area code. Try another area.' : 'Search by area code to find available numbers'}
            </Typography>
          )}
          {quote && <Card variant="soft"><Typography level="title-md">Quote for {formatPhone(quote.phone_number)}</Typography>
            <Typography level="body-sm">Setup ${Number(quote.setup_fee).toFixed(2)} + first month ${Number(quote.monthly_fee).toFixed(2)} = ${Number(quote.total).toFixed(2)} due now. Rental renews at ${Number(quote.monthly_fee).toFixed(2)} per month until provider-confirmed release. Campaign and message fees are separate.</Typography>
            <Typography level="body-xs">Quote expires {new Date(quote.expires_at).toLocaleTimeString()}. A purchase does not guarantee immediate texting activation.</Typography>
            <Checkbox checked={acceptedQuote} disabled={purchaseMutation.isPending} onChange={e => setAcceptedQuote(e.target.checked)} label="I authorize these setup and recurring rental charges from our usage balance." />
            <Button loading={purchaseMutation.isPending} disabled={!acceptedQuote || new Date(quote.expires_at).getTime() <= Date.now()} onClick={() => purchaseMutation.mutate()}>Authorize and rent number</Button>
          </Card>}
        </ModalDialog>
      </Modal>

      {/* Edit Modal */}
      <Modal open={showEditModal} onClose={() => setShowEditModal(false)}>
        <ModalDialog sx={{ width: 550, maxWidth: '95vw', maxHeight: '90vh', overflow: 'auto' }}>
          <ModalClose />
          <Typography level="h4" sx={{ mb: 2 }}>Edit Phone Number</Typography>

          {actionError && <Alert color="danger" sx={{ mb: 2 }}>{actionError}</Alert>}

          {selectedNumber && (
            <Stack spacing={3}>
              <Typography level="body-sm" color="neutral">
                {formatPhone(selectedNumber.phone_number)}
              </Typography>

              {/* Basic Settings */}
              <FormControl>
                <FormLabel>Friendly Name</FormLabel>
                <Input
                  placeholder="e.g. Main Office, Sales Line"
                  value={editFriendlyName}
                  onChange={(e) => setEditFriendlyName(e.target.value)}
                />
              </FormControl>

              <FormControl>
                <FormLabel>Forward Calls To</FormLabel>
                <Input
                  placeholder="e.g. +18005551234 (leave blank for voicemail)"
                  value={editForwardTo}
                  onChange={(e) => setEditForwardTo(e.target.value)}
                />
                <FormHelperText>
                  Rings this number for 25 seconds, then sends the caller to voicemail.
                </FormHelperText>
              </FormControl>

              {editForwardTo.trim() && (
                <FormControl>
                  <Checkbox
                    checked={editScreenCalls}
                    onChange={(e) => setEditScreenCalls(e.target.checked)}
                    label="Ask me to press a key before connecting"
                  />
                  <FormHelperText>
                    Recommended when forwarding to a mobile. Without it your carrier's
                    voicemail answers first and keeps the message, so it never reaches
                    ApexOS. Turn off only when forwarding to a desk phone or answering
                    service that can't press keys.
                  </FormHelperText>
                </FormControl>
              )}

              <FormControl>
                <FormLabel>Voicemail Greeting</FormLabel>
                <Textarea
                  minRows={2}
                  placeholder={'Default: "Hello, you\'ve reached our office. Please leave a message after the beep."'}
                  value={editGreeting}
                  onChange={(e) => setEditGreeting(e.target.value)}
                />
                <FormHelperText>
                  Read to callers before they leave a voicemail. Leave blank to use the default greeting.
                </FormHelperText>
              </FormControl>

              <Divider />

              {/* User Assignments Section */}
              <Box>
                <Typography level="title-md" sx={{ mb: 1 }}>
                  <PersonIcon size={18} style={{ marginRight: 4, verticalAlign: 'text-bottom' }} />
                  Assigned Users
                </Typography>
                <Typography level="body-xs" color="neutral" sx={{ mb: 2 }}>
                  Users assigned to this number can send SMS and make calls from it.
                </Typography>

                {/* Current Assignments */}
                {selectedNumber.assigned_users && selectedNumber.assigned_users.length > 0 ? (
                  <Stack spacing={1} sx={{ mb: 2 }}>
                    {selectedNumber.assigned_users.map((assignment: AssignedUser) => (
                      <Card key={assignment.id} variant="outlined" size="sm">
                        <Stack direction="row" alignItems="center" spacing={1.5}>
                          <Avatar size="sm">
                            {assignment.user_name?.[0]?.toUpperCase() || 'U'}
                          </Avatar>
                          <Box sx={{ flex: 1 }}>
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                              <Typography level="body-sm" fontWeight="md">
                                {assignment.user_name}
                              </Typography>
                              {assignment.is_default && (
                                <StarIcon size={14} color="var(--joy-palette-warning-500)" />
                              )}
                            </Stack>
                            <Stack direction="row" spacing={0.5}>
                              <Checkbox size="sm" label="Texting" checked={assignment.can_send_sms} disabled={editAssignment.isPending} onChange={event => editAssignment.mutate({ id: assignment.id, changes: { can_send_sms: event.target.checked } })} />
                              <Checkbox size="sm" label="Calls" checked={assignment.can_make_calls} disabled={editAssignment.isPending} onChange={event => editAssignment.mutate({ id: assignment.id, changes: { can_make_calls: event.target.checked } })} />
                            </Stack>
                          </Box>
                          <Stack direction="row" spacing={0.5}>
                            {!assignment.is_default && (
                              <Button
                                size="sm"
                                variant="plain"
                                color="neutral"
                                loading={setDefaultMutation.isPending}
                                onClick={() => setDefaultMutation.mutate(assignment.id)}
                              >
                                Set Default
                              </Button>
                            )}
                            <IconButton
                              size="sm"
                              variant="plain"
                              color="danger"
                              loading={removeAssignmentMutation.isPending}
                              onClick={() => removeAssignmentMutation.mutate(assignment.id)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Stack>
                        </Stack>
                      </Card>
                    ))}
                  </Stack>
                ) : (
                  <Typography level="body-sm" color="neutral" sx={{ mb: 2, fontStyle: 'italic' }}>
                    No users assigned to this number yet.
                  </Typography>
                )}

                {/* Add User Assignment */}
                {availableUsers.length > 0 && (
                  <Card variant="soft" size="sm">
                    <Stack spacing={1.5}>
                      <FormControl size="sm">
                        <FormLabel>Add User</FormLabel>
                        <Select
                          placeholder="Select a user..."
                          value={selectedUserId}
                          onChange={(_, value) => setSelectedUserId(value)}
                        >
                          {availableUsers.map((user) => (
                            <Option key={user.id} value={user.id}>
                              {user.full_name || user.email}
                            </Option>
                          ))}
                        </Select>
                      </FormControl>

                      {selectedUserId && (
                        <>
                          <Stack direction="row" spacing={2}>
                            <Checkbox
                              size="sm"
                              checked={assignCanSendSms}
                              onChange={(e) => setAssignCanSendSms(e.target.checked)}
                              label="Can send SMS"
                            />
                            <Checkbox
                              size="sm"
                              checked={assignCanMakeCalls}
                              onChange={(e) => setAssignCanMakeCalls(e.target.checked)}
                              label="Can make calls"
                            />
                          </Stack>
                          <Button
                            size="sm"
                            variant="solid"
                            startDecorator={<AddIcon />}
                            loading={assignUserMutation.isPending}
                            onClick={handleAssignUser}
                            disabled={!assignCanSendSms && !assignCanMakeCalls}
                          >
                            Assign User
                          </Button>
                        </>
                      )}
                    </Stack>
                  </Card>
                )}
              </Box>

              <Divider />

              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button variant="plain" onClick={() => setShowEditModal(false)}>Cancel</Button>
                <Button
                  loading={updateMutation.isPending}
                  onClick={() =>
                    updateMutation.mutate({
                      id: selectedNumber.id,
                      data: {
                        forward_to_number: editForwardTo.trim(),
                        friendly_name: editFriendlyName.trim(),
                        voicemail_greeting: editGreeting.trim(),
                        screen_forwarded_calls: editScreenCalls,
                      },
                    })
                  }
                >
                  Save Changes
                </Button>
              </Stack>
            </Stack>
          )}
        </ModalDialog>
      </Modal>

      {/* Release Confirm Modal */}
      <Modal open={showReleaseConfirm} onClose={() => setShowReleaseConfirm(false)}>
        <ModalDialog sx={{ width: 450, maxWidth: '95vw' }}>
          <ModalClose />
          <Typography level="h4" sx={{ mb: 1 }}>Release Number?</Typography>

          {actionError && <Alert color="danger" sx={{ mb: 2 }}>{actionError}</Alert>}

          {selectedNumber && (
            <Stack spacing={2}>
              <Typography level="body-sm">
                This will permanently release{' '}
                <strong>{formatPhone(selectedNumber.phone_number)}</strong> back to Twilio.
                You will no longer receive SMS or calls on this number. Texting stops when release is requested; rental charges end after the provider confirms release.
              </Typography>
              <FormControl><FormLabel>Type {selectedNumber.phone_number} to confirm</FormLabel><Input value={releaseConfirmation} onChange={event => setReleaseConfirmation(event.target.value)} /></FormControl>
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button variant="plain" onClick={() => setShowReleaseConfirm(false)}>Cancel</Button>
                <Button
                  color="danger"
                  loading={releaseMutation.isPending}
                  disabled={releaseConfirmation !== selectedNumber.phone_number}
                  onClick={() => releaseMutation.mutate(selectedNumber.id)}
                >
                  Release
                </Button>
              </Stack>
            </Stack>
          )}
        </ModalDialog>
      </Modal>
    </Box>
  );
};

export default PhoneNumbersPage;
